import type { OrderWithLines } from "@kanpla/api-contract";

import { autoId } from "~/utils/auto-id";
import { priceFormatter } from "~/utils/price-formatter";
import type { Currency } from "~/api/products/types";
import { type OrderLineWithTotal } from "~/hooks/queries/orders";

export type FormattedOrderLine = {
  id: string;
  name: string;
  orderLineId?: string;
  productLineId?: string;
  unitRefundAmountInclVat: number;
  unitRefundTaxAmount: number;
  formattedPrice: string;
  refunded: boolean;
};

export function formatRefundOrderLines(
  order: OrderWithLines,
  currency: Currency,
  locale: string | null
): FormattedOrderLine[] {
  if (!order.orderLines) return [];

  return order.orderLines.reduce(
    (acc: FormattedOrderLine[], line: OrderLineWithTotal) => {
      if (!line.amount) return acc;

      const discount = order.discounts?.find(
        (discount) =>
          discount.discountType === "discount-hybridBilling" &&
          discount.items.length > 0 &&
          (discount.items[0].orderLineId === line.orderLineId ||
            discount.items[0].productLineId === line.productLineId)
      );

      const unitDiscountedAmount = discount?.unitDiscountedAmount ?? 0;

      const unitRefundAmountInclVat =
        line.orderLineUnitPrice - unitDiscountedAmount / line.amount;

      const unitRefundAmountExclVat =
        line.unitPrice - unitDiscountedAmount / line.amount;

      const unitRefundTaxAmount =
        unitRefundAmountInclVat - unitRefundAmountExclVat;

      if (line.amount === 1) {
        const alreadyRefunded = Boolean(
          order.refunds?.some((refund) =>
            refund.orderLines.some(
              (refundLine) =>
                refundLine.orderLineId === line.orderLineId &&
                refundLine.amount === -1
            )
          )
        );

        return [
          ...acc,
          {
            name: line.name,
            orderLineId: line.orderLineId,
            productLineId: line.productLineId,
            id: autoId(),
            unitRefundAmountInclVat,
            unitRefundTaxAmount,
            formattedPrice: priceFormatter({
              value: unitRefundAmountInclVat,
              currency,
              locale
            }),
            refunded: alreadyRefunded
          }
        ];
      }

      const alreadyRefunded =
        order.refunds?.reduce((acc, refund) => {
          const refundLines = refund.orderLines.filter(
            (refundLine) =>
              refundLine.orderLineId === line.orderLineId ||
              refundLine.productLineId === line.productLineId
          );

          if (!refundLines) return acc;

          return (
            acc +
            refundLines.reduce((acc, refundLine) => {
              if (!refundLine.amount) return acc;

              return acc + -refundLine.amount;
            }, 0)
          );
        }, 0) ?? 0;

      const lines = Array.from({ length: line.amount }, (_, index) => ({
        name: line.name,
        orderLineId: line.orderLineId,
        productLineId: line.productLineId,
        id: autoId(),
        unitRefundAmountInclVat,
        unitRefundTaxAmount,
        formattedPrice: priceFormatter({
          value: unitRefundAmountInclVat,
          currency: order.currency,
          locale
        }),
        refunded: alreadyRefunded > index
      }));

      return [...acc, ...lines];
    },
    []
  );
}
